/**
 * Where the optional patient context (notes / therapist strips and the
 * planning band) sits relative to the calendar grid.
 *
 * Both blocks are conditional: they appear only once a patient is selected and
 * only when that patient actually has notes / open appointments. Rendered
 * ABOVE the grid — the old Scheduler's arrangement — their appearance pushes
 * the whole calendar (toolbar, resource headers, time grid) down, so every
 * patient switch visibly displaces what the user is looking at. Rendered
 * BELOW, the grid stays top-anchored and only its bottom edge moves, which is
 * why "below" is the default here.
 *
 * The preference is a MobiliCal prototype concern, so it lives in localStorage
 * rather than CalendarStore: the old Scheduler keeps its shelf on top
 * unconditionally and must not inherit this.
 */
import { useCallback, useState } from "react";
import type { ShelfPlacement } from "../../molecules/Calendar/EventShelf/EventShelf";

export type { ShelfPlacement };

const STORAGE_KEY = "mobiliCal.shelfPlacement";

const readStoredPlacement = (): ShelfPlacement =>
  localStorage.getItem(STORAGE_KEY) === "above" ? "above" : "below";

export const useShelfPlacement = (): [ShelfPlacement, (next: ShelfPlacement) => void] => {
  const [placement, setPlacement] = useState<ShelfPlacement>(readStoredPlacement);

  const updatePlacement = useCallback((next: ShelfPlacement) => {
    setPlacement(next);
    localStorage.setItem(STORAGE_KEY, next);
  }, []);

  return [placement, updatePlacement];
};

export default useShelfPlacement;
